<template>
  <div class="firmwares-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-upload"></i>
          版本管理
        </h1>
        <p class="page-description">管理固件版本，包括固件上传、查看、删除和一键升级等操作</p>
      </div>
    </div>

    <!-- 搜索和操作区域 -->
    <div class="search-section">
      <el-card class="search-card" shadow="never">
        <div class="search-form">
          <div class="search-row">
            <div class="search-item">
              <el-select
                v-model="searchForm.chip_type"
                placeholder="芯片类型"
                clearable
                class="search-select"
              >
                <el-option label="全部芯片" value="" />
                <el-option label="ESP12E" value="ESP12E" />
              </el-select>
            </div>
            <div class="search-item">
              <el-select
                v-model="searchForm.device_type"
                placeholder="设备类型"
                clearable
                class="search-select"
              >
                <el-option label="全部设备" value="" />
                <el-option label="单开型" value="single" />
                <el-option label="双开型" value="double" />
              </el-select>
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.version"
                placeholder="搜索版本号"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-actions">
              <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                搜索
              </el-button>
              <el-button icon="el-icon-refresh" @click="handleReset">
                重置
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card class="action-card" shadow="never">
        <div class="action-buttons">
          <el-button
            type="primary"
            icon="el-icon-upload"
            @click="showUploadDialog"
          >
            上传固件
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 固件列表 -->
    <div class="table-section">
      <el-card class="table-card" shadow="never">
        <el-table
          v-loading="loading"
          :data="firmwareList"
          style="width: 100%"
          stripe
        >
          <el-table-column prop="ID" label="ID" width="80" align="center" />
          <el-table-column prop="version_string" label="版本字符串" min-width="200" align="center" />
          <el-table-column prop="chip_type" label="芯片类型" width="120" align="center" />
          <el-table-column prop="device_type" label="设备类型" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.device_type === 'single'" type="success" size="small">单开型</el-tag>
              <el-tag v-else-if="scope.row.device_type === 'double'" type="warning" size="small">双开型</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="version" label="版本号" width="100" align="center" />
          <el-table-column label="发布范围" width="130" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.is_gray" type="warning" size="small">
                灰度发布（{{ scope.row.gray_user_count || 0 }}人）
              </el-tag>
              <el-tag v-else type="success" size="small">全量发布</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="file_size" label="文件大小" width="100">
            <template slot-scope="scope">
              {{ formatFileSize(scope.row.file_size) }}
            </template>
          </el-table-column>
          <el-table-column prop="checksum" label="哈希值" width="140" align="center">
            <template slot-scope="scope">
              <el-tooltip :content="scope.row.checksum" placement="top">
                <span class="checksum-text">{{ formatChecksum(scope.row.checksum) }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="CreatedAt" label="上传时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.CreatedAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="390" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="small"
                icon="el-icon-view"
                @click="showDetailDialog(scope.row)"
              >
                详情
              </el-button>
              <el-button
                type="text"
                size="small"
                icon="el-icon-edit"
                @click="showReleaseDialog(scope.row)"
              >
                编辑
              </el-button>
              <el-button
                type="text"
                size="small"
                icon="el-icon-download"
                @click="handleDownload(scope.row)"
              >
                下载
              </el-button>
              <el-button
                type="text"
                size="small"
                icon="el-icon-upload2"
                @click="showUpgradeDialog(scope.row)"
              >
                一键升级
              </el-button>
              <el-button
                type="text"
                size="small"
                icon="el-icon-delete"
                class="danger-text"
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.page_size"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 上传固件对话框 -->
    <el-dialog
      title="上传固件"
      :visible.sync="uploadDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="uploadForm"
        :model="uploadForm"
        :rules="uploadRules"
        label-width="120px"
      >
        <el-form-item label="芯片类型" prop="chip_type">
          <el-select v-model="uploadForm.chip_type" placeholder="请选择芯片类型" style="width: 100%" @change="handleChipTypeChange">
            <el-option label="ESP12E" value="ESP12E" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备类型" prop="device_type">
          <el-select v-model="uploadForm.device_type" placeholder="请选择设备类型" style="width: 100%" @change="handleDeviceTypeChange">
            <el-option label="单开型机器人" value="single" />
            <el-option label="双开型机器人" value="double" />
          </el-select>
        </el-form-item>
        <el-form-item label="版本号" prop="version">
          <div style="margin-bottom: 10px">
            <el-checkbox v-model="manualVersionMode" @change="handleManualVersionModeChange">
              手动输入版本号
            </el-checkbox>
          </div>
          <el-input
            v-model="uploadForm.version"
            placeholder="例如: 1.0.0"
            @input="handleVersionInput"
            :disabled="!manualVersionMode && !extractedVersion"
          >
            <template slot="prepend">v</template>
          </el-input>
          <div v-if="manualVersionMode" class="version-type-radio" style="margin-top: 10px">
            <el-radio-group v-model="versionType" @change="handleVersionTypeChange" :disabled="!canAutoGenerateVersion || manualVersionEdit">
              <el-radio label="major">版本升级 (主版本+1)</el-radio>
              <el-radio label="minor">特性更新 (次版本+1)</el-radio>
              <el-radio label="patch">修订补丁 (修订号+1)</el-radio>
            </el-radio-group>
          </div>
          <div v-if="manualVersionMode && !canAutoGenerateVersion && !manualVersionEdit" class="version-hint">
            <i class="el-icon-info"></i> 请先选择芯片类型和设备类型
          </div>
          <div v-if="manualVersionMode && manualVersionEdit" class="version-hint">
            <i class="el-icon-info"></i> 已手动修改版本号，自动生成已禁用
          </div>
          <div v-if="!manualVersionMode && extractedVersion" class="version-hint version-success">
            <i class="el-icon-success"></i> 已从固件文件中提取版本号: {{ extractedVersion }}
          </div>
          <div v-if="!manualVersionMode && versionExtractionError" class="version-hint version-error">
            <i class="el-icon-error"></i> {{ versionExtractionError }}
          </div>
        </el-form-item>
        <el-form-item label="更新说明">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="5"
            placeholder="支持Markdown格式，描述本次更新的内容"
          />
        </el-form-item>
        <el-form-item label="发布范围" prop="release_scope">
          <el-radio-group v-model="uploadForm.release_scope" @change="handleReleaseScopeChange">
            <el-radio label="public">全量发布</el-radio>
            <el-radio label="gray">灰度发布</el-radio>
          </el-radio-group>
          <div class="release-scope-hint">
            全量发布对所有用户可见；灰度发布仅对选中的用户和管理员可见。
          </div>
        </el-form-item>
        <el-form-item
          v-if="uploadForm.release_scope === 'gray'"
          label="灰度用户"
          prop="gray_user_ids"
          :rules="[{ required: true, type: 'array', min: 1, message: '请至少选择一个灰度用户', trigger: 'change' }]"
        >
          <el-select
            v-model="uploadForm.gray_user_ids"
            multiple
            filterable
            remote
            reserve-keyword
            clearable
            :remote-method="searchGrayUsers"
            :loading="grayUserLoading"
            placeholder="输入用户名或邮箱搜索"
            style="width: 100%"
          >
            <el-option
              v-for="user in grayUserOptions"
              :key="user.id"
              :label="formatGrayUserOption(user)"
              :value="user.id"
            />
          </el-select>
          <div class="release-scope-hint">仅显示状态正常的用户，管理员无需加入灰度名单。</div>
        </el-form-item>
        <el-form-item label="固件文件" prop="file">
          <el-upload
            ref="upload"
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            :limit="1"
            accept=".bin"
            action="#"
          >
            <el-button slot="trigger" size="small" type="primary">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">只能上传.bin文件，且不超过50MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="handleUpload">上传</el-button>
      </div>
    </el-dialog>

    <!-- 编辑发布范围对话框 -->
    <el-dialog
      title="编辑发布范围"
      :visible.sync="releaseDialogVisible"
      width="560px"
      custom-class="release-dialog"
      :close-on-click-modal="false"
      :before-close="handleReleaseDialogBeforeClose"
      @closed="handleReleaseDialogClosed"
    >
      <div v-loading="releaseDetailLoading" class="release-dialog-body">
        <el-form
          ref="releaseForm"
          :model="releaseForm"
          :rules="releaseRules"
          label-width="90px"
        >
          <el-form-item label="固件版本">
            <span>{{ releaseFirmware ? releaseFirmware.version_string : '-' }}</span>
          </el-form-item>
          <el-form-item label="发布范围" prop="release_scope">
            <el-radio-group v-model="releaseForm.release_scope" @change="handleEditReleaseScopeChange">
              <el-radio label="public">全量发布</el-radio>
              <el-radio label="gray">灰度发布</el-radio>
            </el-radio-group>
            <div class="release-scope-hint">
              全量发布对所有用户可见；灰度发布仅对选中的用户和管理员可见。
            </div>
          </el-form-item>
          <el-form-item
            v-if="releaseForm.release_scope === 'gray'"
            label="灰度用户"
            prop="gray_user_ids"
          >
            <el-select
              v-model="releaseForm.gray_user_ids"
              multiple
              filterable
              remote
              reserve-keyword
              clearable
              :remote-method="searchReleaseGrayUsers"
              :loading="releaseGrayUserLoading"
              placeholder="输入用户名或邮箱搜索"
              style="width: 100%"
            >
              <el-option
                v-for="user in releaseGrayUserOptions"
                :key="user.id"
                :label="formatGrayUserOption(user)"
                :value="user.id"
                :disabled="user.status !== 1"
              />
            </el-select>
            <div v-if="releaseInvalidGrayUsers.length" class="release-scope-error">
              已有灰度用户被禁用，请先从已选列表中移除后再保存。
            </div>
            <div class="release-scope-hint">仅显示状态正常的用户，管理员无需加入灰度名单。</div>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button :disabled="releaseSaving" @click="closeReleaseDialog">取消</el-button>
        <el-button
          type="primary"
          :loading="releaseSaving"
          :disabled="releaseDetailLoading"
          @click="handleReleaseSave"
        >
          保存
        </el-button>
      </div>
    </el-dialog>

    <!-- 固件详情对话框 -->
    <el-dialog
      title="固件详情"
      :visible.sync="detailDialogVisible"
      width="700px"
    >
      <div v-loading="detailLoading" class="firmware-detail-loading">
        <div v-if="currentFirmware" class="firmware-detail">
          <el-descriptions :column="2" border>
          <el-descriptions-item label="版本字符串">
            {{ currentFirmware.version_string }}
          </el-descriptions-item>
          <el-descriptions-item label="版本号">
            v{{ currentFirmware.version }}
          </el-descriptions-item>
          <el-descriptions-item label="芯片类型">
            {{ currentFirmware.chip_type }}
          </el-descriptions-item>
          <el-descriptions-item label="设备类型">
            <el-tag v-if="currentFirmware.device_type === 'single'" type="success" size="small">单开型</el-tag>
            <el-tag v-else-if="currentFirmware.device_type === 'double'" type="warning" size="small">双开型</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="文件大小">
            {{ formatFileSize(currentFirmware.file_size) }}
          </el-descriptions-item>
          <el-descriptions-item label="文件名">
            {{ currentFirmware.file_name }}
          </el-descriptions-item>
          <el-descriptions-item label="哈希值" :span="2">
            <span style="font-family: monospace; word-break: break-all;">{{ currentFirmware.checksum }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="上传者">
            {{ currentFirmware.uploaded_by_name }}
          </el-descriptions-item>
          <el-descriptions-item label="上传时间">
            {{ formatDate(currentFirmware.CreatedAt) }}
          </el-descriptions-item>
          <el-descriptions-item label="发布范围" :span="2">
            <el-tag v-if="currentFirmware.is_gray" type="warning" size="small">灰度发布</el-tag>
            <el-tag v-else type="success" size="small">全量发布</el-tag>
            <span v-if="currentFirmware.is_gray" class="gray-user-count">
              共 {{ currentFirmware.gray_user_count }} 名用户
            </span>
          </el-descriptions-item>
          </el-descriptions>

          <div v-if="currentFirmware.is_gray" class="gray-user-detail">
            <h4>灰度用户</h4>
            <div v-if="currentFirmware.gray_users.length" class="gray-user-list">
              <el-tag
                v-for="user in currentFirmware.gray_users"
                :key="user.id"
                type="info"
                class="gray-user-tag"
              >
                {{ user.username }}（ID: {{ user.id }}）<span v-if="user.email"> · {{ user.email }}</span>
              </el-tag>
            </div>
            <el-alert
              v-else
              title="该灰度版本暂未配置有效用户"
              type="warning"
              :closable="false"
              show-icon
            />
          </div>

          <div v-if="currentFirmware.description" class="firmware-description">
            <h4>更新说明</h4>
            <div class="markdown-content" v-html="renderMarkdown(currentFirmware.description)"></div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 一键升级对话框 -->
    <el-dialog
      title="一键升级"
      :visible.sync="upgradeDialogVisible"
      width="1000px"
      :close-on-click-modal="false"
    >
      <div v-if="upgradeStep === 1">
        <div class="upgrade-info">
          <p><strong>固件版本：</strong>{{ currentFirmware ? currentFirmware.version_string : '' }}</p>
          <p><strong>设备类型：</strong>{{ currentFirmware ? (currentFirmware.device_type === 'single' ? '单开型' : '双开型') : '' }}</p>
          <p><strong>芯片类型：</strong>{{ currentFirmware ? currentFirmware.chip_type : '' }}</p>
        </div>
        <el-divider></el-divider>
        
        <!-- 筛选表单 -->
        <div class="upgrade-filter">
          <div class="filter-row">
            <div class="filter-item">
              <span class="filter-label">设备ID</span>
              <el-input v-model="upgradeFilterForm.robot_id" placeholder="设备ID" clearable size="mini" />
            </div>
            <div class="filter-item">
              <span class="filter-label">设备名称</span>
              <el-input v-model="upgradeFilterForm.robot_name" placeholder="设备名称" clearable size="mini" />
            </div>
            <div class="filter-item">
              <span class="filter-label">用户ID</span>
              <el-input v-model="upgradeFilterForm.user_id" placeholder="用户ID" clearable size="mini" />
            </div>
            <div class="filter-item">
              <span class="filter-label">用户名称</span>
              <el-input v-model="upgradeFilterForm.username" placeholder="用户名称" clearable size="mini" />
            </div>
            <div class="filter-item">
              <span class="filter-label">在线状态</span>
              <el-select v-model="upgradeFilterForm.online" placeholder="全部" clearable size="mini">
                <el-option label="全部" value="" />
                <el-option label="在线" value="true" />
                <el-option label="离线" value="false" />
              </el-select>
            </div>
            <el-button type="primary" size="mini" @click="handleUpgradeSearch">搜索</el-button>
            <el-button size="mini" @click="handleUpgradeFilterReset">重置</el-button>
          </div>
        </div>
        
        <h4>选择要升级的设备 <span style="color: #909399; font-weight: normal; font-size: 12px;">(共 {{ matchingRobots.length }} 台设备)</span></h4>
        <el-table
          ref="upgradeTable"
          v-loading="loadingRobots"
          :data="matchingRobots"
          @selection-change="handleRobotSelection"
          style="width: 100%"
          max-height="350"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="ID" label="设备ID" width="80" />
          <el-table-column prop="name" label="设备名称" min-width="120" />
          <el-table-column prop="user_id" label="用户ID" width="80" />
          <el-table-column prop="username" label="用户名称" min-width="100" />
          <el-table-column prop="firmware_ver" label="当前版本" width="100">
            <template slot-scope="scope">
              {{ scope.row.firmware_ver || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="online" label="在线状态" width="80">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.online" type="success" size="small">在线</el-tag>
              <el-tag v-else type="info" size="small">离线</el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="matchingRobots.length === 0 && !loadingRobots" class="empty-hint">
          <i class="el-icon-warning"></i> 没有找到匹配的设备
        </div>
      </div>

      <div v-if="upgradeStep === 2">
        <div class="upgrade-confirm">
          <h4>确认升级信息</h4>
          <p><strong>固件版本：</strong>{{ currentFirmware ? currentFirmware.version_string : '' }}</p>
          <p><strong>选中设备数量：</strong>{{ selectedRobots.length }}</p>
          <el-divider></el-divider>
          <h4>设备列表</h4>
          <ul class="robot-list">
            <li v-for="robot in selectedRobots" :key="robot.ID">
              {{ robot.name }} ({{ robot.mqtt_topic }})
            </li>
          </ul>
          <el-alert
            title="确认后将向选中的设备发送固件更新指令"
            type="warning"
            :closable="false"
            style="margin-top: 20px"
          />
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button v-if="upgradeStep === 1" @click="upgradeDialogVisible = false">取消</el-button>
        <el-button v-if="upgradeStep === 1" type="primary" :disabled="selectedRobots.length === 0" @click="upgradeStep = 2">
          下一步
        </el-button>
        <el-button v-if="upgradeStep === 2" @click="upgradeStep = 1">上一步</el-button>
        <el-button v-if="upgradeStep === 2" type="primary" :loading="upgrading" @click="handleUpgradeConfirm">
          确认升级
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getFirmwareList, uploadFirmware, overwriteFirmware, getFirmwareDetail, updateFirmwareRelease, deleteFirmware, upgradeFirmware, getMatchingRobots, getFirmwareDownloadUrl } from '@/api/firmware'
import { getUserList } from '@/api/user'
import { getToken } from '@/utils/auth'
import { marked } from 'marked'

export default {
  name: 'AdminFirmwares',
  data() {
    return {
      loading: false,
      firmwareList: [],
      pagination: {
        page: 1,
        page_size: 10,
        total: 0
      },
      searchForm: {
        chip_type: '',
        device_type: '',
        version: ''
      },
      uploadDialogVisible: false,
      uploadForm: {
        chip_type: '',
        device_type: '',
        version: '',
        description: '',
        release_scope: 'public',
        gray_user_ids: []
      },
      uploadRules: {
        chip_type: [{ required: true, message: '请选择芯片类型', trigger: 'change' }],
        device_type: [{ required: true, message: '请选择设备类型', trigger: 'change' }],
        version: [{ required: true, message: '请输入版本号', trigger: 'blur' }],
        release_scope: [{ required: true, message: '请选择发布范围', trigger: 'change' }]
      },
      grayUserOptions: [],
      grayUserLoading: false,
      grayUserRequestId: 0,
      releaseDialogVisible: false,
      releaseDetailLoading: false,
      releaseSaving: false,
      releaseFirmware: null,
      releaseForm: {
        release_scope: 'public',
        gray_user_ids: []
      },
      releaseRules: {
        release_scope: [{ required: true, message: '请选择发布范围', trigger: 'change' }],
        gray_user_ids: [{ required: true, type: 'array', min: 1, message: '请至少选择一个灰度用户', trigger: 'change' }]
      },
      releaseGrayUserOptions: [],
      releaseGrayUserLoading: false,
      releaseGrayUserRequestId: 0,
      releaseDialogSessionId: 0,
      fileList: [],
      uploading: false,
      versionType: 'patch',
      manualVersionEdit: false,
      manualVersionMode: false,
      extractedVersion: null,
      versionExtractionError: null,
      latestVersion: null,
      detailDialogVisible: false,
      detailLoading: false,
      currentFirmware: null,
      upgradeDialogVisible: false,
      upgradeStep: 1,
      matchingRobots: [],
      selectedRobots: [],
      loadingRobots: false,
      upgrading: false,
      upgradeFilterForm: {
        robot_id: '',
        robot_name: '',
        user_id: '',
        username: '',
        online: 'true'
      }
    }
  },
  computed: {
    // 是否可以自动生成版本号
    canAutoGenerateVersion() {
      return this.uploadForm.chip_type && this.uploadForm.device_type
    },
    releaseInvalidGrayUsers() {
      if (this.releaseForm.release_scope !== 'gray') return []
      return this.releaseGrayUserOptions.filter(user => (
        this.releaseForm.gray_user_ids.includes(user.id) && user.status !== 1
      ))
    }
  },
  mounted() {
    this.fetchFirmwareList()
  },
  methods: {
    // 获取固件列表
    async fetchFirmwareList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          page_size: this.pagination.page_size
        }
        const response = await getFirmwareList(params)
        this.firmwareList = response.data.list || []
        this.pagination.total = response.data.total || 0
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.fetchFirmwareList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        chip_type: '',
        device_type: '',
        version: ''
      }
      this.pagination.page = 1
      this.fetchFirmwareList()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.page_size = val
      this.pagination.page = 1
      this.fetchFirmwareList()
    },

    // 页码改变
    handlePageChange(val) {
      this.pagination.page = val
      this.fetchFirmwareList()
    },

    // 显示上传对话框
    async showUploadDialog() {
      this.uploadDialogVisible = true
      this.uploadForm = {
        chip_type: '',
        device_type: '',
        version: '',
        description: '',
        release_scope: 'public',
        gray_user_ids: []
      }
      this.grayUserRequestId++
      this.grayUserOptions = []
      this.grayUserLoading = false
      this.fileList = []
      this.versionType = 'patch'
      this.manualVersionEdit = false
      this.manualVersionMode = false
      this.extractedVersion = null
      this.versionExtractionError = null
      this.latestVersion = null
      
      // 获取最新版本号
      await this.fetchLatestVersion()
      
      // 自动生成版本号
      this.generateNextVersion()
    },

    // 获取最新版本号
    async fetchLatestVersion() {
      if (!this.uploadForm.chip_type || !this.uploadForm.device_type) {
        return
      }
      
      try {
        const response = await getFirmwareList({
          chip_type: this.uploadForm.chip_type,
          device_type: this.uploadForm.device_type,
          page: 1,
          page_size: 1
        })
        
        if (response.data.list && response.data.list.length > 0) {
          this.latestVersion = response.data.list[0].version
        } else {
          this.latestVersion = null
        }
      } catch (error) {
        console.error('获取最新版本失败:', error)
      }
    },

    // 生成下一个版本号
    generateNextVersion() {
      // 在手动模式下，如果用户手动编辑过版本号，则不自动生成
      if (!this.manualVersionMode && this.manualVersionEdit) {
        return
      }
      
      let version = '1.0.0'
      
      if (this.latestVersion) {
        const parts = this.latestVersion.split('.')
        let major = parseInt(parts[0]) || 0
        let minor = parseInt(parts[1]) || 0
        let patch = parseInt(parts[2]) || 0
        
        if (this.versionType === 'major') {
          major++
          minor = 0
          patch = 0
        } else if (this.versionType === 'minor') {
          minor++
          patch = 0
        } else {
          patch++
        }
        
        version = `${major}.${minor}.${patch}`
      }
      
      this.uploadForm.version = version
    },

    // 芯片类型改变
    async handleChipTypeChange() {
      this.manualVersionEdit = false
      await this.fetchLatestVersion()
      
      if (this.manualVersionMode) {
        // 手动模式：重新生成版本号
        this.generateNextVersion()
      } else if (!this.extractedVersion) {
        // 自动模式：如果还没有提取到版本号，不做任何操作
        // 等待用户选择文件后自动提取
      }
    },

    // 设备类型改变
    async handleDeviceTypeChange() {
      this.manualVersionEdit = false
      await this.fetchLatestVersion()
      
      if (this.manualVersionMode) {
        // 手动模式：重新生成版本号
        this.generateNextVersion()
      } else if (!this.extractedVersion) {
        // 自动模式：如果还没有提取到版本号，不做任何操作
        // 等待用户选择文件后自动提取
      }
    },

    // 版本号输入处理
    handleVersionInput() {
      if (this.manualVersionMode) {
        this.manualVersionEdit = true
      }
    },

    // 版本类型改变
    handleVersionTypeChange() {
      this.manualVersionEdit = false
      this.generateNextVersion()
    },

    // 文件选择
    async handleFileChange(file, fileList) {
      this.fileList = fileList
      
      // 如果不是手动模式，尝试从文件中提取版本号
      if (!this.manualVersionMode && fileList.length > 0) {
        await this.extractVersionFromFile(file.raw)
      }
    },

    // 从二进制文件中提取版本号
    async extractVersionFromFile(file) {
      this.extractedVersion = null
      this.versionExtractionError = null
      
      try {
        // 读取文件内容
        const arrayBuffer = await file.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        
        // 将字节数组转换为字符串进行搜索
        const decoder = new TextDecoder('utf-8', { fatal: false })
        const content = decoder.decode(uint8Array)
        
        // 搜索 FWVER: 标记 FWVER:vX.X.X
        const versionPattern = /FWVER:v(\d+\.\d+\.\d+)/
        const match = content.match(versionPattern)
        
        if (match && match[1]) {
          const version = match[1]
          this.extractedVersion = version
          this.uploadForm.version = version
          this.manualVersionEdit = false
        } else {
          this.versionExtractionError = '未在固件文件中找到版本信息 (FWVER:vX.X.X)'
          this.uploadForm.version = ''
        }
      } catch (error) {
        console.error('提取版本号失败:', error)
        this.versionExtractionError = '读取固件文件失败: ' + error.message
        this.uploadForm.version = ''
      }
    },

    // 手动版本模式切换
    handleManualVersionModeChange(value) {
      if (value) {
        // 切换到手动模式 - 启用版本生成按钮
        this.extractedVersion = null
        this.versionExtractionError = null
        this.manualVersionEdit = false
        // 自动生成版本号
        this.generateNextVersion()
      } else {
        // 切换到自动模式 - 从文件提取版本
        this.uploadForm.version = ''
        if (this.fileList.length > 0) {
          // 如果已经选择了文件，重新提取版本号
          this.extractVersionFromFile(this.fileList[0].raw)
        }
      }
    },

    // 发布范围切换
    handleReleaseScopeChange(scope) {
      if (scope === 'public') {
        this.uploadForm.gray_user_ids = []
        this.grayUserOptions = []
        this.grayUserRequestId++
      } else {
        this.searchGrayUsers('')
      }

      this.$nextTick(() => {
        if (this.$refs.uploadForm) {
          this.$refs.uploadForm.clearValidate('gray_user_ids')
        }
      })
    },

    // 远程搜索可参与灰度发布的有效用户
    async searchGrayUsers(query) {
      const requestId = ++this.grayUserRequestId
      this.grayUserLoading = true

      try {
        const response = await getUserList({
          page: 1,
          pageSize: 30,
          keyword: query ? query.trim() : '',
          status: 1
        })

        if (requestId !== this.grayUserRequestId) return

        const users = response.data && Array.isArray(response.data.list) ? response.data.list : []
        const fetchedUsers = users
          .map(user => ({
            id: Number(user.id),
            username: typeof user.username === 'string' ? user.username : '',
            email: typeof user.email === 'string' ? user.email : '',
            status: Number(user.status)
          }))
          .filter(user => Number.isInteger(user.id) && user.id > 1 && user.status === 1)
        const selectedUsers = this.grayUserOptions.filter(user => this.uploadForm.gray_user_ids.includes(user.id))
        const userMap = new Map(selectedUsers.map(user => [user.id, user]))
        fetchedUsers.forEach(user => userMap.set(user.id, user))
        this.grayUserOptions = Array.from(userMap.values())
      } finally {
        if (requestId === this.grayUserRequestId) {
          this.grayUserLoading = false
        }
      }
    },

    formatGrayUserOption(user) {
      const email = user.email ? `，${user.email}` : ''
      const status = user.status === 1 ? '' : '，已禁用'
      return `${user.username || '未命名用户'}（ID: ${user.id}${email}${status}）`
    },

    normalizeGrayUser(user) {
      return {
        id: Number(user.id),
        username: typeof user.username === 'string' ? user.username : '',
        email: typeof user.email === 'string' ? user.email : '',
        status: user.status === undefined ? 1 : Number(user.status)
      }
    },

    async fetchGrayUserOptions(query, selectedUsers, requestId, isCurrentRequest) {
      const response = await getUserList({
        page: 1,
        pageSize: 30,
        keyword: query ? query.trim() : '',
        status: 1
      })
      if (!isCurrentRequest(requestId)) return null

      const users = response.data && Array.isArray(response.data.list) ? response.data.list : []
      const fetchedUsers = users
        .map(this.normalizeGrayUser)
        .filter(user => Number.isInteger(user.id) && user.id > 1 && user.status === 1)
      const userMap = new Map(selectedUsers.map(user => [user.id, user]))
      fetchedUsers.forEach(user => userMap.set(user.id, user))
      return Array.from(userMap.values())
    },

    async showReleaseDialog(firmware) {
      const sessionId = ++this.releaseDialogSessionId
      this.releaseDialogVisible = true
      this.releaseDetailLoading = true
      this.releaseSaving = false
      this.releaseFirmware = firmware
      this.releaseForm = { release_scope: 'public', gray_user_ids: [] }
      this.releaseGrayUserOptions = []
      this.releaseGrayUserRequestId++
      this.releaseGrayUserLoading = false

      try {
        const response = await getFirmwareDetail(firmware.ID)
        if (sessionId !== this.releaseDialogSessionId || !this.releaseDialogVisible) return
        const detail = this.normalizeFirmwareDetail(response.data || {})
        this.releaseFirmware = detail
        this.releaseForm = {
          release_scope: detail.is_gray ? 'gray' : 'public',
          gray_user_ids: detail.gray_users.map(user => user.id)
        }
        this.releaseGrayUserOptions = detail.gray_users.map(this.normalizeGrayUser)
      } catch (error) {
        if (sessionId !== this.releaseDialogSessionId) return
        console.error('获取固件发布范围失败:', error)
        this.releaseDialogVisible = false
        this.$message.error('获取固件发布范围失败')
      } finally {
        if (sessionId === this.releaseDialogSessionId) {
          this.releaseDetailLoading = false
          this.$nextTick(() => {
            if (this.$refs.releaseForm) this.$refs.releaseForm.clearValidate()
          })
        }
      }
    },

    handleEditReleaseScopeChange(scope) {
      if (scope === 'public') {
        this.releaseForm.gray_user_ids = []
        this.releaseGrayUserOptions = []
        this.releaseGrayUserRequestId++
        this.releaseGrayUserLoading = false
      } else {
        this.searchReleaseGrayUsers('')
      }
      this.$nextTick(() => {
        if (this.$refs.releaseForm) this.$refs.releaseForm.clearValidate('gray_user_ids')
      })
    },

    async searchReleaseGrayUsers(query) {
      const requestId = ++this.releaseGrayUserRequestId
      this.releaseGrayUserLoading = true
      const selectedUsers = this.releaseGrayUserOptions.filter(user => this.releaseForm.gray_user_ids.includes(user.id))
      try {
        const options = await this.fetchGrayUserOptions(
          query,
          selectedUsers,
          requestId,
          id => id === this.releaseGrayUserRequestId
        )
        if (options) this.releaseGrayUserOptions = options
      } finally {
        if (requestId === this.releaseGrayUserRequestId) this.releaseGrayUserLoading = false
      }
    },

    invalidateReleaseDialogRequests() {
      this.releaseDialogSessionId++
      this.releaseGrayUserRequestId++
      this.releaseDetailLoading = false
      this.releaseGrayUserLoading = false
    },

    closeReleaseDialog() {
      if (this.releaseSaving) return
      this.invalidateReleaseDialogRequests()
      this.releaseDialogVisible = false
    },

    handleReleaseDialogBeforeClose(done) {
      if (this.releaseSaving) return
      this.invalidateReleaseDialogRequests()
      done()
    },

    handleReleaseDialogClosed() {
      if (this.releaseDialogVisible) return
      this.invalidateReleaseDialogRequests()
      this.releaseFirmware = null
    },

    handleReleaseSave() {
      if (this.releaseInvalidGrayUsers.length) {
        this.$message.warning('请先移除已禁用的灰度用户')
        return
      }
      this.$refs.releaseForm.validate(async valid => {
        if (!valid || !this.releaseFirmware) return
        const sessionId = this.releaseDialogSessionId
        const firmwareID = this.releaseFirmware.ID
        this.releaseSaving = true
        let updated = null
        try {
          const response = await updateFirmwareRelease(firmwareID, {
            release_scope: this.releaseForm.release_scope,
            gray_user_ids: this.releaseForm.release_scope === 'gray' ? this.releaseForm.gray_user_ids : []
          })
          updated = this.normalizeFirmwareDetail(response.data || {})
          const index = this.firmwareList.findIndex(item => item.ID === updated.ID)
          if (index !== -1) {
            this.$set(this.firmwareList, index, { ...this.firmwareList[index], ...updated })
          }
          if (sessionId === this.releaseDialogSessionId) {
            this.releaseSaving = false
            this.releaseDialogVisible = false
          }
          this.$message.success('发布范围已更新')
        } catch (error) {
          console.error('修改固件发布范围失败:', error)
          this.$message.error('修改固件发布范围失败')
        } finally {
          if (sessionId === this.releaseDialogSessionId) this.releaseSaving = false
        }

        if (updated) {
          try {
            await this.fetchFirmwareList()
          } catch (error) {
            console.error('刷新固件列表失败:', error)
            this.$message.warning('发布范围已更新，但列表刷新失败，请手动刷新')
          }
        }
      })
    },

    // 上传固件
    async handleUpload() {
      this.$refs.uploadForm.validate(async (valid) => {
        if (!valid) {
          return
        }
        
        if (this.fileList.length === 0) {
          this.$message.error('请选择固件文件')
          return
        }
        
        const file = this.fileList[0].raw
        if (!file.name.endsWith('.bin')) {
          this.$message.error('只支持.bin格式的固件文件')
          return
        }
        
        if (file.size > 50 * 1024 * 1024) {
          this.$message.error('文件大小不能超过50MB')
          return
        }
        
        const formData = new FormData()
        formData.append('chip_type', this.uploadForm.chip_type)
        formData.append('device_type', this.uploadForm.device_type)
        formData.append('version', this.uploadForm.version)
        formData.append('description', this.uploadForm.description)
        formData.append('manual_mode', this.manualVersionMode ? 'true' : 'false')
        formData.append('release_scope', this.uploadForm.release_scope)
        formData.append(
          'gray_user_ids',
          JSON.stringify(this.uploadForm.release_scope === 'gray' ? this.uploadForm.gray_user_ids : [])
        )
        formData.append('file', file)
        
        this.uploading = true
        try {
          const response = await uploadFirmware(formData)
          
          // 检查是否有警告信息
          if (response.warning) {
            // 先显示警告信息
            this.$message({
              message: response.warning,
              type: 'warning',
              duration: 6000
            })
            // 延迟显示成功信息，避免重叠
            setTimeout(() => {
              this.$message.success('固件上传成功')
            }, 500)
          } else {
            this.$message.success('固件上传成功')
          }
          
          this.uploadDialogVisible = false
          this.fetchFirmwareList()
        } catch (error) {
          // 检查是否是版本冲突
          if (error.response && error.response.status === 409) {
            this.$confirm('该版本固件已存在，是否覆盖？', '提示', {
              confirmButtonText: '覆盖',
              cancelButtonText: '取消',
              type: 'warning'
            }).then(async () => {
              const existingId = error.response.data.data.existing_firmware.ID
              formData.set('manual_mode', this.manualVersionMode ? 'true' : 'false')
              const overwriteResponse = await overwriteFirmware(existingId, formData)
              
              // 检查是否有警告信息
              if (overwriteResponse.warning) {
                // 先显示警告信息
                this.$message({
                  message: overwriteResponse.warning,
                  type: 'warning',
                  duration: 6000
                })
                // 延迟显示成功信息，避免重叠
                setTimeout(() => {
                  this.$message.success('固件覆盖成功')
                }, 500)
              } else {
                this.$message.success('固件覆盖成功')
              }
              
              this.uploadDialogVisible = false
              this.fetchFirmwareList()
            })
          }
        } finally {
          this.uploading = false
        }
      })
    },

    // 显示详情对话框
    async showDetailDialog(firmware) {
      this.currentFirmware = null
      this.detailDialogVisible = true
      this.detailLoading = true

      try {
        const response = await getFirmwareDetail(firmware.ID)
        this.currentFirmware = this.normalizeFirmwareDetail(response.data || {})
      } catch (error) {
        console.error('获取固件详情失败:', error)
        this.detailDialogVisible = false
        this.$message.error('获取固件详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    normalizeFirmwareDetail(firmware) {
      const grayUsers = Array.isArray(firmware.gray_users) ? firmware.gray_users : []
      const safeGrayUsers = grayUsers
        .map(user => ({
          id: Number(user.id),
          username: typeof user.username === 'string' ? user.username : '',
          email: typeof user.email === 'string' ? user.email : '',
          status: user.status === undefined ? 1 : Number(user.status)
        }))
        .filter(user => Number.isInteger(user.id) && user.id > 0)
      const grayUserCount = Number(firmware.gray_user_count)

      return {
        ...firmware,
        is_gray: firmware.is_gray === true,
        gray_users: safeGrayUsers,
        gray_user_count: Number.isFinite(grayUserCount) && grayUserCount >= 0
          ? grayUserCount
          : safeGrayUsers.length
      }
    },

    // 显示升级对话框
    async showUpgradeDialog(firmware) {
      this.currentFirmware = firmware
      this.upgradeDialogVisible = true
      this.upgradeStep = 1
      this.selectedRobots = []
      // 重置筛选表单，默认在线状态为在线
      this.upgradeFilterForm = {
        robot_id: '',
        robot_name: '',
        user_id: '',
        username: '',
        online: 'true'
      }
      
      // 获取匹配的机器人
      await this.fetchMatchingRobots()
    },

    // 获取匹配的机器人列表
    async fetchMatchingRobots() {
      if (!this.currentFirmware) return
      
      this.loadingRobots = true
      try {
        const response = await getMatchingRobots(this.currentFirmware.ID, this.upgradeFilterForm)
        this.matchingRobots = response.data.robots || []
      } finally {
        this.loadingRobots = false
      }
    },

    // 升级筛选搜索
    handleUpgradeSearch() {
      this.selectedRobots = []
      this.fetchMatchingRobots()
    },

    // 升级筛选重置
    handleUpgradeFilterReset() {
      this.upgradeFilterForm = {
        robot_id: '',
        robot_name: '',
        user_id: '',
        username: '',
        online: 'true'
      }
      this.selectedRobots = []
      this.fetchMatchingRobots()
    },

    // 机器人选择改变
    handleRobotSelection(selection) {
      this.selectedRobots = selection
    },

    // 确认升级
    async handleUpgradeConfirm() {
      if (this.selectedRobots.length === 0) {
        this.$message.warning('请选择要升级的设备')
        return
      }
      
      this.upgrading = true
      try {
        const robotIds = this.selectedRobots.map(r => r.ID)
        const response = await upgradeFirmware(this.currentFirmware.ID, { robot_ids: robotIds })
        
        const result = response.data
        if (result.failure_count > 0) {
          this.$message.warning(`升级完成：成功 ${result.success_count} 个，失败 ${result.failure_count} 个`)
        } else {
          this.$message.success(`升级指令已发送到 ${result.success_count} 个设备`)
        }
        
        this.upgradeDialogVisible = false
      } finally {
        this.upgrading = false
      }
    },

    // 下载固件
    async handleDownload(firmware) {
      const token = getToken()
      const downloadUrl = getFirmwareDownloadUrl(firmware.ID)
      
      try {
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (!response.ok) {
          throw new Error('下载失败')
        }
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${firmware.version_string}.bin`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error('下载固件失败: ' + error.message)
      }
    },

    // 删除固件
    handleDelete(firmware) {
      this.$confirm(`确定要删除固件 ${firmware.version_string} 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteFirmware(firmware.ID)
        this.$message.success('删除成功')
        this.fetchFirmwareList()
      })
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },

    // 渲染Markdown
    renderMarkdown(text) {
      if (!text) return ''
      return marked(text)
    },

    // 格式化哈希值（显示前8位和后8位）
    formatChecksum(checksum) {
      if (!checksum) return '-'
      if (checksum.length <= 16) return checksum
      return `${checksum.substring(0, 8)}...${checksum.substring(checksum.length - 8)}`
    }
  }
}
</script>

<style scoped>
.firmwares-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.page-title i {
  margin-right: 8px;
  color: #409EFF;
}

.page-description {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.search-section,
.action-section,
.table-section {
  margin-bottom: 20px;
}

.search-card,
.action-card,
.table-card {
  border-radius: 4px;
}

.search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.search-item {
  flex: 0 0 auto;
}

.search-input,
.search-select {
  width: 200px;
}

.search-actions {
  margin-left: auto;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.danger-text {
  color: #F56C6C;
}

.checksum-text {
  font-family: monospace;
  font-size: 12px;
  cursor: pointer;
}

.version-hint {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
}

.version-success {
  color: #67C23A;
}

.version-error {
  color: #F56C6C;
}

.release-scope-hint {
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.release-scope-error {
  margin-top: 5px;
  color: #F56C6C;
  font-size: 12px;
  line-height: 1.5;
}

@media (max-width: 600px) {
  ::v-deep .release-dialog {
    width: calc(100% - 24px) !important;
  }
}

.firmware-detail-loading {
  min-height: 160px;
}

.firmware-detail {
  padding: 10px 0;
}

.firmware-description {
  margin-top: 20px;
}

.firmware-description h4 {
  margin-bottom: 10px;
  color: #303133;
}

.gray-user-count {
  margin-left: 8px;
  color: #606266;
}

.gray-user-detail {
  margin-top: 20px;
}

.gray-user-detail h4 {
  margin-bottom: 10px;
  color: #303133;
}

.gray-user-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gray-user-tag {
  height: auto;
  max-width: 100%;
  white-space: normal;
  word-break: break-all;
}

.markdown-content {
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  line-height: 1.6;
}

.upgrade-info {
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 15px;
}

.upgrade-info p {
  margin: 8px 0;
}

.upgrade-filter {
  margin-bottom: 15px;
  padding: 12px 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.filter-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.filter-label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.filter-item .el-input,
.filter-item .el-select {
  width: 100px;
}

.empty-hint {
  text-align: center;
  padding: 40px;
  color: #909399;
}

.empty-hint i {
  font-size: 48px;
  margin-bottom: 10px;
  display: block;
}

.upgrade-confirm {
  padding: 10px 0;
}

.robot-list {
  list-style: none;
  padding: 0;
  margin: 10px 0;
  max-height: 300px;
  overflow-y: auto;
}

.robot-list li {
  padding: 8px 12px;
  background-color: #f5f7fa;
  margin-bottom: 8px;
  border-radius: 4px;
}
</style>
